import { useKeenSlider } from 'keen-slider/react'
import React, {
  Children,
  isValidElement,
  useState,
  useRef,
  useEffect,
} from 'react'
import cn from 'clsx'
import s from './ProductSlider.module.scss'
import ProductSliderControl from '../ProductSliderControl'
import { BREAKPOINT_MD } from '../../../siteConfig'

interface ProductSliderProps {
  children: React.ReactNode[]
  className?: string
  childClassName?: string
  disabledOnDesktop?: boolean
  currentSlide?: number
  showNavigation?: boolean
  perView?: number
  spacing?: number
  mobileProps?: any
}

const ProductSlider: React.FC<ProductSliderProps> = ({
  children,
  className = '',
  childClassName = '',
  disabledOnDesktop = true,
  currentSlide,
  showNavigation = true,
  perView = 1,
  spacing = 0,
  mobileProps = {},
}) => {
  // const [currentSlide, setCurrentSlide] = useState(0)
  const [isMounted, setIsMounted] = useState(false)
  const sliderContainerRef = useRef<HTMLDivElement>(null)
  const thumbsContainerRef = useRef<HTMLDivElement>(null)

  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView, spacing },
    created: () => setIsMounted(true),
    disabled: false,
    breakpoints: {
      [`(max-width: ${BREAKPOINT_MD - 1}px)`]: {
        ...mobileProps,
      },
      [`(min-width: ${BREAKPOINT_MD}px)`]: {
        disabled: disabledOnDesktop,
      },
    },
    slideChanged(s) {
      const slideNumber = s.track.details.rel
      // setCurrentSlide(slideNumber)

      if (thumbsContainerRef.current) {
        const $el = document.getElementById(`thumb-${slideNumber}`)
        if (slideNumber >= 3) {
          thumbsContainerRef.current.scrollLeft = $el!.offsetLeft
        } else {
          thumbsContainerRef.current.scrollLeft = 0
        }
      }
    },
  })

  // Stop the history navigation gesture on touch devices
  useEffect(() => {
    const preventNavigation = (event: TouchEvent) => {
      // Center point of the touch area
      const touchXPosition = event.touches[0].pageX
      // Size of the touch area
      const touchXRadius = event.touches[0].radiusX || 0

      // We set a threshold (10px) on both sizes of the screen,
      // if the touch area overlaps with the screen edges
      // it's likely to trigger the navigation. We prevent the
      // touchstart event in that case.
      if (
        touchXPosition - touchXRadius < 10 ||
        touchXPosition + touchXRadius > window.innerWidth - 10
      )
        event.preventDefault()
    }

    const slider = sliderContainerRef.current!

    slider.addEventListener('touchstart', preventNavigation)

    return () => {
      if (slider) {
        slider.removeEventListener('touchstart', preventNavigation)
      }
    }
  }, [])

  useEffect(() => {
    if (slider.current) {
      if (currentSlide != undefined) {
        slider.current.moveToIdx(currentSlide)
      }
    }
  }, [currentSlide])

  const onPrev = React.useCallback(() => slider.current?.prev(), [slider])
  const onNext = React.useCallback(() => slider.current?.next(), [slider])

  return (
    <div className={cn(s.root, className)} ref={sliderContainerRef}>
      <div
        ref={ref}
        className={cn(
          s.slider,
          { [s.show]: isMounted },
          childClassName,
          'keen-slider'
        )}
      >
        {isMounted &&
          slider &&
          slider.current &&
          !slider.current.options.disabled &&
          showNavigation && (
            <ProductSliderControl onPrev={onPrev} onNext={onNext} />
          )}
        {Children.map(children, (child) => {
          // Add the keen-slider__slide className to children
          if (isValidElement(child)) {
            return {
              ...child,
              props: {
                ...child.props,
                className: `${
                  child.props.className ? `${child.props.className} ` : ''
                } keen-slider__slide`,
              },
            }
          }
          return child
        })}
      </div>
    </div>
  )
}

export default ProductSlider
